/**
 * Rendering helpers for the two-language payload the API returns.
 *
 * The whole point of shipping EN and DE together is that switching language is a
 * re-render, not a refetch. These helpers read the active i18next language, so a
 * component using them updates the moment the shell's language selector changes
 * and no request is made.
 */

import { useTranslation } from "react-i18next";
import type { LocalizedText } from "@models/releaseNotes";

export type SupportedLanguage = "en" | "de";

/** Narrow an i18next language tag ("de-DE", "de") to the two we carry. */
export function toSupportedLanguage(language: string): SupportedLanguage {
  return language.toLowerCase().startsWith("de") ? "de" : "en";
}

export function pickLocalized(
  text: LocalizedText | undefined,
  language: string,
): string {
  if (!text) return "";
  const preferred = text[toSupportedLanguage(language)];
  // Fall back to the other language rather than rendering an empty row. The
  // publish endpoint requires both, so this only fires for content that predates
  // that rule or was written straight into the table.
  return preferred?.trim() ? preferred : (text.en?.trim() ?? "");
}

/** Returns a picker bound to the active language, plus a date formatter. */
export function useLocalized() {
  const { i18n } = useTranslation();
  const language = i18n.language || "en";

  return {
    language,
    pick: (text: LocalizedText | undefined) => pickLocalized(text, language),
    formatDate: (isoDate: string) => formatReleaseDate(isoDate, language),
  };
}

/**
 * Dates are formatted through Intl for the active locale, never hand-built:
 * German readers expect 14.07.2026 where English readers expect 14 Jul 2026.
 */
export function formatReleaseDate(isoDate: string, language: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat(language, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}
